import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AttachFile as AttachFileIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { ordersAPI } from '../../services/api';
import { useCurrency } from '../../contexts/CurrencyContext';
import FileUpload from './FileUpload';

const TestOrder = ({ website, onClose }) => {
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [orderConfig, setOrderConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [includeFiles, setIncludeFiles] = useState(false);
  const [products, setProducts] = useState([
    {
      id: '1',
      name: 'Sample Product 1',
      description: 'This is a sample product for testing',
      price: 29.99,
      quantity: 1,
      category: 'Electronics',
      sku: 'SP001'
    },
    {
      id: '2',
      name: 'Sample Product 2',
      description: 'Another sample product',
      price: 49.99,
      quantity: 2,
      category: 'Accessories',
      sku: 'SP002'
    }
  ]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      notes: '',
      paymentMethod: 'card',
      paymentStatus: 'pending'
    }
  });

  const { control: productControl, handleSubmit: handleProductSubmit, reset: resetProduct, formState: { errors: productErrors } } = useForm({
    defaultValues: {
      name: '',
      description: '',
      price: 0,
      quantity: 1,
      category: '',
      sku: ''
    }
  });

  // Load order configuration
  useEffect(() => {
    const loadOrderConfig = async () => {
      if (!website?.siteId) return;

      try {
        setConfigLoading(true);
        const response = await ordersAPI.getOrderConfig(website.siteId);
        setOrderConfig(response.data.website);
      } catch (error) {
        console.error('Failed to load order config:', error);
        toast.error('Failed to load order configuration');
      } finally {
        setConfigLoading(false);
      }
    };

    loadOrderConfig();
  }, [website?.siteId]);

  const calculateTotals = () => {
    const subtotal = products.reduce((sum, product) => sum + (product.price * product.quantity), 0);
    const tax = subtotal * 0.08; // 8% tax for demo
    const shipping = subtotal > 50 ? 0 : 9.99; // Free shipping over 50
    const total = subtotal + tax + shipping;

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      tax: parseFloat(tax.toFixed(2)),
      shipping: parseFloat(shipping.toFixed(2)),
      discount: 0,
      total: parseFloat(total.toFixed(2))
    };
  };

  const onSubmit = async (data) => {
    if (!website?.siteId) {
      toast.error('No website selected');
      return;
    }

    if (products.length === 0) {
      toast.error('Please add at least one product to the order');
      return;
    }

    try {
      setLoading(true);
      setSubmissionResult(null);

      // Filter out empty fields
      const customerData = Object.entries(data)
        .filter(([key, value]) => value && value.toString().trim() !== '')
        .reduce((acc, [key, value]) => {
          acc[key] = typeof value === 'string' ? value.trim() : value;
          return acc;
        }, {});

      const totals = calculateTotals();
      const paymentInfo = {
        method: data.paymentMethod,
        status: data.paymentStatus
      };

      let response;
      if (includeFiles && files.length > 0) {
        response = await ordersAPI.submitOrderWithFiles(
          website.siteId,
          customerData,
          products,
          totals,
          paymentInfo,
          files
        );
      } else {
        response = await ordersAPI.submitOrder(website.siteId, {
          customerData,
          products,
          totals,
          paymentInfo
        });
      }

      setSubmissionResult({
        success: true,
        data: response.data,
        submittedData: { customerData, products, totals, paymentInfo }
      });

      toast.success('Order submitted successfully!');

    } catch (error) {
      console.error('Order submission error:', error);

      setSubmissionResult({
        success: false,
        error: error.response?.data || { message: 'Unknown error occurred' },
        submittedData: { customerData: data, products, totals: calculateTotals() }
      });

      toast.error(error.response?.data?.message || 'Order submission failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    reset();
    setSubmissionResult(null);
    setFiles([]);
    setIncludeFiles(false);
    setProducts([
      {
        id: '1',
        name: 'Sample Product 1',
        description: 'This is a sample product for testing',
        price: 29.99,
        quantity: 1,
        category: 'Electronics',
        sku: 'SP001'
      },
      {
        id: '2',
        name: 'Sample Product 2',
        description: 'Another sample product',
        price: 49.99,
        quantity: 2,
        category: 'Accessories',
        sku: 'SP002'
      }
    ]);
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    resetProduct();
    setProductDialogOpen(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    resetProduct(product);
    setProductDialogOpen(true);
  };

  const handleDeleteProduct = (productId) => {
    setProducts(products.filter(p => p.id !== productId));
  };

  const onProductSubmit = (data) => {
    const productData = {
      ...data,
      id: editingProduct ? editingProduct.id : Date.now().toString(),
      price: parseFloat(data.price),
      quantity: parseInt(data.quantity)
    };

    if (editingProduct) {
      setProducts(products.map(p => p.id === editingProduct.id ? productData : p));
    } else {
      setProducts([...products, productData]);
    }

    setProductDialogOpen(false);
    resetProduct();
    setEditingProduct(null);
  };

  if (configLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading order configuration...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!orderConfig) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Failed to load order configuration. Please check if the website exists and is active.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const totals = calculateTotals();

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h2">
            Test Order: {orderConfig.name}
          </Typography>
          <Box>
            <Tooltip title="Reset order">
              <IconButton onClick={handleReset} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Order Info */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Box display="flex" alignItems="center" mb={1}>
            <InfoIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle2">Order Information</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Site ID:</strong> {orderConfig.siteId}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Total Items:</strong> {products.reduce((sum, p) => sum + p.quantity, 0)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Order Total:</strong> {formatCurrency(totals.total)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Products:</strong> {products.length}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Products Section */}
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
              <ShoppingCartIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Products</Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddProduct}
              size="small"
            >
              Add Product
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {product.name}
                        </Typography>
                        {product.description && (
                          <Typography variant="caption" color="text.secondary">
                            {product.description}
                          </Typography>
                        )}
                        {product.sku && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            SKU: {product.sku}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(product.price)}</TableCell>
                    <TableCell align="right">{product.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(product.price * product.quantity)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEditProduct(product)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteProduct(product.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Order Totals */}
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Box minWidth={200}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2">{formatCurrency(totals.subtotal)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Tax (8%):</Typography>
                <Typography variant="body2">{formatCurrency(totals.tax)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Shipping:</Typography>
                <Typography variant="body2">{formatCurrency(totals.shipping)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6" color="primary">{formatCurrency(totals.total)}</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Customer Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Full Name"
                      fullWidth
                      size="small"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email'
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email"
                      type="email"
                      fullWidth
                      size="small"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Phone"
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="paymentMethod"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>Payment Method</InputLabel>
                      <Select {...field} label="Payment Method">
                        <MenuItem value="card">Credit Card</MenuItem>
                        <MenuItem value="paypal">PayPal</MenuItem>
                        <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Address"
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="City"
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="State"
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="zipCode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="ZIP Code"
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Order Notes"
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* File Upload Section */}
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <AttachFileIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Order Attachments</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeFiles}
                    onChange={(e) => setIncludeFiles(e.target.checked)}
                  />
                }
                label="Include files"
                sx={{ ml: 2 }}
              />
            </Box>

            {includeFiles && (
              <FileUpload
                files={files}
                onFilesChange={setFiles}
                maxFiles={5}
                maxSize={10 * 1024 * 1024} // 10MB
              />
            )}
          </Paper>

          {/* Submit Button */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
            >
              Reset Order
            </Button>

            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              disabled={loading || products.length === 0}
              size="large"
            >
              {loading ? 'Submitting...' : 'Submit Order'}
            </Button>
          </Box>
        </form>

        {/* Submission Result */}
        {submissionResult && (
          <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              {submissionResult.success ? (
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
              ) : (
                <ErrorIcon color="error" sx={{ mr: 1 }} />
              )}
              <Typography variant="h6">
                {submissionResult.success ? 'Order Submitted Successfully!' : 'Order Submission Failed'}
              </Typography>
            </Box>

            {submissionResult.success ? (
              <Box>
                <Typography variant="body2" gutterBottom>
                  <strong>Order Number:</strong> {submissionResult.data.orderNumber}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Total Amount:</strong> {formatCurrency(submissionResult.data.totalAmount)}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Total Items:</strong> {submissionResult.data.totalItems}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Email Sent:</strong> {submissionResult.data.email?.sent ? 'Yes' : 'No'}
                </Typography>
                {submissionResult.data.filesProcessed > 0 && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Files Processed:</strong> {submissionResult.data.filesProcessed}
                  </Typography>
                )}
              </Box>
            ) : (
              <Alert severity="error">
                {submissionResult.error.message}
              </Alert>
            )}
          </Paper>
        )}

        {/* Product Dialog */}
        <Dialog open={productDialogOpen} onClose={() => setProductDialogOpen(false)} maxWidth="sm" fullWidth>
          <form onSubmit={handleProductSubmit(onProductSubmit)}>
            <DialogTitle>
              {editingProduct ? 'Edit Product' : 'Add Product'}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Controller
                    name="name"
                    control={productControl}
                    rules={{ required: 'Product name is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Product Name"
                        fullWidth
                        error={!!productErrors.name}
                        helperText={productErrors.name?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={productControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        fullWidth
                        multiline
                        rows={2}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="price"
                    control={productControl}
                    rules={{
                      required: 'Price is required',
                      min: { value: 0.01, message: 'Price must be greater than 0' }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Price"
                        type="number"
                        fullWidth
                        inputProps={{ step: 0.01, min: 0 }}
                        error={!!productErrors.price}
                        helperText={productErrors.price?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="quantity"
                    control={productControl}
                    rules={{
                      required: 'Quantity is required',
                      min: { value: 1, message: 'Quantity must be at least 1' }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Quantity"
                        type="number"
                        fullWidth
                        inputProps={{ min: 1 }}
                        error={!!productErrors.quantity}
                        helperText={productErrors.quantity?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="category"
                    control={productControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Category"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="sku"
                    control={productControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="SKU"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setProductDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="contained">
                {editingProduct ? 'Update' : 'Add'} Product
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default TestOrder; 